import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, X } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { errText, getMyActivity, getMyCodes, getMyOrders, getMyPoints, getRewards, giftArt, redeemReward, updateMe } from "@/lib/api";
import { cad } from "@/lib/site";

const date = (s) => (s ? new Date(s).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "");
const Box = ({ children, className = "", ...rest }) => <div className={`rounded-[24px] border-[1.5px] border-ink p-6 sm:p-8 ${className}`} {...rest}>{children}</div>;
const Empty = ({ children }) => <p className="text-ink/60">{children}</p>;

export const Profile = () => {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user.name);
  const save = async (body) => { try { setUser(await updateMe(body)); toast.success("Saved"); } catch (e) { toast.error(errText(e)); } };
  return (
    <Box data-testid="account-profile">
      <div className="grid gap-6 md:grid-cols-2">
        <label className="block"><span className="label text-ink/60">Name</span><input value={name} onChange={(e) => setName(e.target.value)} className="field" data-testid="profile-name" /></label>
        <div><p className="label text-ink/60">Email</p><p className="mt-3" data-testid="profile-email">{user.email} <span className={`label ml-2 rounded-full px-2 py-0.5 !text-[10px] ${user.email_verified ? "bg-lime" : "bg-pink"}`}>{user.email_verified ? "verified" : "check your inbox to verify"}</span></p></div>
      </div>
      <button onClick={() => save({ name })} className="btn btn-ink mt-6" data-testid="profile-save">Save</button>
      <label className="mt-8 flex items-center gap-3 border-t border-ink/10 pt-6 text-sm"><Switch checked={user.newsletter} onCheckedChange={(v) => save({ newsletter: v })} data-testid="profile-newsletter" /> Sus Crew newsletter</label>
      <p className="label mt-8 text-ink/60">Saved addresses</p>
      {user.addresses.length ? (
        <ul className="mt-3 grid gap-3 md:grid-cols-2">{user.addresses.map((a, i) => (
          <li key={a.line1 + i} className="flex justify-between gap-3 rounded-[16px] bg-ink/5 p-4 text-sm" data-testid={`profile-address-${i}`}>
            <span><b>{a.label}</b><br />{a.line1}{a.line2 && `, ${a.line2}`}<br />{a.city}, {a.province} {a.postal_code}</span>
            <button onClick={() => save({ addresses: user.addresses.filter((_, j) => j !== i) })} aria-label="Remove address" data-testid={`profile-address-remove-${i}`}><X size={16} /></button>
          </li>))}</ul>
      ) : <Empty>Tick "Save this address" at checkout and it lands here.</Empty>}
      <p className="mt-8 text-sm text-ink/60">Signed in with: {user.providers.join(", ")}</p>
    </Box>
  );
};

export const Orders = () => {
  const [d, setD] = useState(null);
  useEffect(() => { getMyOrders().then(setD).catch(() => setD({ orders: [] })); }, []);
  if (!d) return <Empty>Loading…</Empty>;
  return (
    <Box data-testid="account-orders">
      {!d.guest_linked && <p className="mb-5 rounded-[12px] bg-sky/40 px-4 py-3 text-sm">Verify your email to see guest orders you placed with it before you had an account.</p>}
      {d.orders.length ? <ul className="divide-y divide-ink/10">{d.orders.map((o) => (
        <li key={o.id} className="flex flex-wrap items-center justify-between gap-3 py-4" data-testid={`account-order-${o.order_number}`}>
          <div><p className="font-mono font-bold">{o.order_number}</p><p className="text-sm text-ink/60">{date(o.created_at)} · {o.items.reduce((a, i) => a + i.qty, 0)} items</p></div>
          <span className="label rounded-full bg-lime px-3 py-1 font-bold">{o.status}</span>
          <span>{cad(o.total)}</span>
          {o.tracking && <a href={o.tracking.url} target="_blank" rel="noreferrer" className="link-u">Track</a>}
        </li>))}</ul> : <Empty>No orders yet. <Link to="/shop" className="link-u">Go shopping</Link></Empty>}
    </Box>
  );
};

export const Points = () => {
  const [d, setD] = useState(null);
  useEffect(() => { getMyPoints().then(setD).catch(() => {}); }, []);
  if (!d) return <Empty>Loading…</Empty>;
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Box className="bg-lime"><p className="label">Crew Points</p><p className="display mt-3 text-7xl" data-testid="points-balance">{d.balance}</p>
        <ul className="font-a mt-6 space-y-1 text-[15px]"><li>Sign up +{d.rules.signup}</li><li>Vote +{d.rules.vote}</li><li>Share +{d.rules.share} (max {d.rules.share_daily_cap}/day)</li><li>{d.rules.per_dollar} pt per C$1 spent</li></ul></Box>
      <Box className="lg:col-span-2" data-testid="points-history"><p className="label font-bold">History</p>
        <ul className="mt-4 divide-y divide-ink/10">{d.history.map((h, i) => (
          <li key={i} className="flex justify-between py-3 text-sm"><span>{h.reason} <span className="text-ink/50">· {date(h.created_at)}</span></span><b className={h.points < 0 ? "text-alert" : ""}>{h.points > 0 ? `+${h.points}` : h.points}</b></li>))}</ul></Box>
    </div>
  );
};

export const Rewards = () => {
  const { user, setUser } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [codes, setCodes] = useState([]);
  const load = () => { getRewards().then(setRewards); getMyCodes().then(setCodes); };
  useEffect(load, []);
  const redeem = async (r) => {
    try { const d = await redeemReward(r.id); setUser({ ...user, points: d.balance }); toast.success(d.code.type === "stickers" ? "Unlocked. Download your crew-only stickers." : `Your code: ${d.code.code}`); load(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3" data-testid="rewards-list">{rewards.map((r, i) => (
        <Box key={r.id} className={["bg-pink", "bg-sky", "bg-lime"][i % 3]} data-testid={`reward-${r.id}`}>
          <p className="display text-4xl">{r.name}</p><p className="label mt-3 font-bold">{r.cost} pts</p>
          <button onClick={() => redeem(r)} disabled={user.points < r.cost} className="btn btn-ink mt-6 w-full" data-testid={`reward-redeem-${r.id}`}>{user.points < r.cost ? `${r.cost - user.points} pts to go` : "Redeem"}</button>
        </Box>))}</div>
      <Box data-testid="reward-codes"><p className="label font-bold">My codes</p>
        {codes.length ? <ul className="mt-4 divide-y divide-ink/10">{codes.map((c) => (
          <li key={c.code} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm" data-testid={`reward-code-${c.code}`}>
            <span className="font-mono font-bold">{c.code}</span><span>{c.name}</span>
            {c.download_url ? <a href={c.download_url} className="link-u inline-flex items-center gap-1" data-testid={`reward-download-${c.code}`}><Download size={13} /> Download</a>
              : <span className="text-ink/60">{c.used_at ? `Used on ${c.order_number || "an order"}` : `Valid until ${date(c.expires_at)} · single use`}</span>}
          </li>))}</ul> : <Empty>Redeem a reward to get a code. Discount codes apply at checkout, one per order.</Empty>}
      </Box>
    </div>
  );
};

export const Activity = () => {
  const [d, setD] = useState(null);
  useEffect(() => { getMyActivity().then(setD).catch(() => {}); }, []);
  if (!d) return <Empty>Loading…</Empty>;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Box className="lg:col-span-2" data-testid="account-gift-cards"><p className="label font-bold">Gift cards</p>
        {d.gift_cards.length ? <ul className="mt-4 grid gap-4 md:grid-cols-2">{d.gift_cards.map((g) => (
          <li key={g.code} className="flex gap-4 rounded-[16px] bg-ink/5 p-3"><img src={giftArt(g.design, g.initial)} alt="" className="w-32 rounded-[10px]" />
            <div className="text-sm"><p className="font-mono font-bold">{g.code}</p><p>{g.role === "received" ? `From ${g.sender_name}` : `To ${g.recipient_name}`} · {g.status}</p><p>Balance {cad(g.balance)} of {cad(g.initial)}</p></div></li>))}</ul>
          : <Empty>No gift cards yet. <Link to="/gift-cards" className="link-u">Send one</Link></Empty>}</Box>
      <Box data-testid="account-alerts"><p className="label font-bold">Restock alerts</p>
        {d.alerts.length ? <ul className="mt-4 divide-y divide-ink/10">{d.alerts.map((a, i) => <li key={i} className="flex justify-between py-3 text-sm"><Link to={`/product/${a.product_slug}`} className="link-u">{a.product_name}</Link><span>{a.color} / {a.size} · {a.status}</span></li>)}</ul> : <Empty>Tap a sold-out size on any product to get one.</Empty>}</Box>
      <Box data-testid="account-waitlists"><p className="label font-bold">Drop waitlists</p>
        {d.waitlists.length ? <ul className="mt-4 divide-y divide-ink/10">{d.waitlists.map((w) => <li key={w.drop_slug} className="flex justify-between py-3 text-sm"><Link to={`/drops/${w.drop_slug}`} className="link-u">Drop {w.number} {w.name}</Link><span>{w.release_label}</span></li>)}</ul> : <Empty>Join a drop waitlist from any upcoming drop page.</Empty>}</Box>
    </div>
  );
};
